import { useEffect, useMemo, useState } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { useProctorSignalR } from "@/utils/useProctorSignalR";
import { applyProctorLiveUpdate } from "@/utils/applyProctorLiveUpdate";
import { displayClientIp } from "@/utils/displayClientIp";
import Helpers from "../../Helpers";
import { getLanguageAbbrev, getLanguageDisplayName, getLanguageId, type LanguageLike } from "@/utils/languageUtils";
import {
  PREFERRED_DATA_KEY_ORDER,
  SCORING_DATA_KEYS,
  formatDataKeyLabel,
  normalizeFieldKey,
  resolveRealtimeDataKey,
} from "@/utils/testMonitorHeaderFields";
import "@/style/sass/_testMonitorModal.scss";

type MonitorSection = {
  sectionId?: number;
  index?: number;
  formId?: number;
  pages?: number;
  isCounted?: boolean;
  questionCount?: number;
  answerCount?: number;
  name?: string;
  submitTime?: string;
};

type MonitorResponse = {
  sectionId?: number;
  questionId?: string | number;
  response?: string | number;
  when?: string;
};

type MonitorRealtime = {
  sectionId?: number;
  formId?: number;
  question?: string;
  response?: string;
  time?: string;
};

type MonitorSite = {
  id?: number;
  name?: string;
  alias?: string;
};

type MonitorJob = {
  id?: number;
  name?: string;
};

type MonitorTest = {
  id?: number;
  code?: string;
  language?: string;
  languageId?: number | string;
  firstName?: string;
  lastName?: string;
  company?: string;
  testTypeName?: string;
  submitTime?: string;
  position?: string;
  location?: string;
  initials?: string;
  reportRecipient?: string;
  uniqueId?: string;
  birthCity?: string;
  birthCountry?: string;
  billingSource?: string;
  facility?: string;
  charge?: string;
  paroleOfficer?: string;
  email?: string;
  companyTestType?: { id?: number; name?: string };
  instanceDataActiveByKey?: Record<string, string | null | undefined>;
  availableSites?: MonitorSite[];
  availableJobs?: MonitorJob[];
  sections?: MonitorSection[];
  responses?: MonitorResponse[];
  realtimeResponses?: MonitorRealtime[];
};

type QuestionHistory = {
  sectionId: number;
  questionId: string;
  finalPosted?: { value: string; when?: string };
  realtimeEntries: Array<{ value: string; when?: string }>;
  previousSelections: string[];
  latestRealtime?: string;
  isChanged: boolean;
};

function normalizeValue(value: unknown) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function dedupeStrings(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)));
}

function buildAnswerId(formId: unknown, questionId: unknown) {
  const formValue = Number(formId || 0);
  const rawQuestion = normalizeValue(questionId).trim();
  if (!rawQuestion) return "";

  if (formValue <= 0) {
    return rawQuestion;
  }

  const match = rawQuestion.match(/^(\d+)([a-zA-Z]*)$/);
  if (!match) {
    return rawQuestion;
  }

  const numericPart = Number(match[1] || 0);
  const suffix = normalizeValue(match[2]);
  if (!Number.isFinite(numericPart) || numericPart <= 0) {
    return rawQuestion;
  }

  return `${formValue + numericPart}${suffix}`;
}

function normalizeLanguageLabel(value: unknown, languages: LanguageLike[] = []) {
  const raw = normalizeValue(value).trim();
  if (!raw) return "";

  const key = normalizeFieldKey(raw);
  const numericId = Number(key);
  if (Number.isFinite(numericId) && numericId > 0) {
    const match = languages.find((lang) => getLanguageId(lang) === numericId);
    if (match) return getLanguageDisplayName(match);
  }

  const byAbbrev = languages.find((lang) => getLanguageAbbrev(lang).toLowerCase() === key);
  if (byAbbrev) return getLanguageDisplayName(byAbbrev);

  const byIetf = languages.find((lang) => String(lang?.ietf || lang?.Ietf || lang?.IETF || "").toLowerCase() === key);
  if (byIetf) return getLanguageDisplayName(byIetf);

  return raw;
}

function isMonitorVisibleSection(section: MonitorSection) {
  if (!section) return false;
  if (section.isCounted === false) return false;
  const normalizedName = normalizeFieldKey(section.name);
  if (normalizedName === "welcome" || normalizedName === "done") return false;
  return true;
}

export default function TestMonitorModal(props) {
  const { isOpen, onClose, testInstanceId, updateSpinner } = props || {};
  const [testData, setTestData] = useState<MonitorTest | null>(null);
  const [supportedLanguages, setSupportedLanguages] = useState<LanguageLike[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  /** When true, selection tracks the taker's active section. Disabled after the viewer clicks a card. */
  const [followActiveSection, setFollowActiveSection] = useState(true);

  async function loadSnapshot(showSpinner = false) {
    if (!testInstanceId) return;
    if (showSpinner) updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync(`/TestDetailsInfo/${testInstanceId}`);
      if (resp?.ok && resp.jsonData) {
        setTestData(resp.jsonData);
      }
    } finally {
      if (showSpinner) updateSpinner?.(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setSupportedLanguages([]);
      return;
    }

    let active = true;
    async function loadLanguagesAsync() {
      const resp = await RightTimeApi.getAsync("/Languages");
      if (!active) return;
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setSupportedLanguages(resp.jsonData);
      } else {
        setSupportedLanguages([]);
      }
    }

    void loadLanguagesAsync();
    return () => {
      active = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !testInstanceId) {
      return;
    }

    setFollowActiveSection(true);
    setSelectedSectionId(null);
    setTestData(null);

    let active = true;
    async function loadInitialSnapshot() {
      if (!active) return;
      await loadSnapshot(true);
    }

    loadInitialSnapshot();

    const refreshTimer = window.setInterval(() => {
      if (active) {
        loadSnapshot(false);
      }
    }, 5000);

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [isOpen, testInstanceId]);

  useProctorSignalR(isOpen, (message) => {
    setTestData((prev) => applyProctorLiveUpdate(prev, message) ?? prev);
  });

  const questionHistories = useMemo(() => {
    const grouped = new Map<string, QuestionHistory>();
    const responses = testData?.responses || [];
    const realtimeResponses = (testData?.realtimeResponses || [])
      .slice()
      .sort((a, b) => new Date(a.time || 0).getTime() - new Date(b.time || 0).getTime());

    realtimeResponses.forEach((item) => {
      if (!item.sectionId || !item.question || item.question === "NextSection" || item.question === "NextPage" || item.question === "Restarted") {
        return;
      }
      const questionId = normalizeValue(item.question);
      const key = `${item.sectionId}:${questionId}`;
      const entry = grouped.get(key) || {
        sectionId: item.sectionId,
        questionId,
        realtimeEntries: [],
        previousSelections: [],
        isChanged: false,
      };
      entry.realtimeEntries.push({
        value: normalizeValue(item.response),
        when: item.time,
      });
      grouped.set(key, entry);
    });

    responses.forEach((item) => {
      if (!item.sectionId || item.questionId === undefined || item.questionId === null) {
        return;
      }
      const questionId = normalizeValue(item.questionId);
      const key = `${item.sectionId}:${questionId}`;
      const entry = grouped.get(key) || {
        sectionId: item.sectionId,
        questionId,
        realtimeEntries: [],
        previousSelections: [],
        isChanged: false,
      };
      entry.finalPosted = {
        value: normalizeValue(item.response),
        when: item.when,
      };
      grouped.set(key, entry);
    });

    grouped.forEach((entry) => {
      const realtimeValues = entry.realtimeEntries.map((item) => item.value);
      const finalValue = entry.finalPosted?.value;
      entry.latestRealtime = realtimeValues.length > 0 ? realtimeValues[realtimeValues.length - 1] : undefined;
      entry.previousSelections = dedupeStrings(
        realtimeValues.filter((value) => value && value !== finalValue),
      );
      entry.isChanged = entry.previousSelections.length > 0;
    });

    return Array.from(grouped.values()).sort((a, b) => a.questionId.localeCompare(b.questionId));
  }, [testData]);

  const sectionRealtimeMeta = useMemo(() => {
    const meta = new Map<number, { currentPage: number; oopsCount: number; latestAt: string | null }>();
    const sorted = (testData?.realtimeResponses || [])
      .filter((item) => item?.sectionId)
      .slice()
      .sort((a, b) => new Date(a.time || 0).getTime() - new Date(b.time || 0).getTime());

    sorted.forEach((item) => {
      const sectionId = Number(item.sectionId || 0);
      if (!sectionId) return;

      const prev = meta.get(sectionId) || { currentPage: 1, oopsCount: 0, latestAt: null };
      const next = { ...prev };

      if (item.question === "NextPage") {
        const parsed = Number(item.response || 0);
        if (parsed > 0) {
          next.currentPage = parsed;
        }
      }

      if (item.question === "NextSection") {
        const parsedOops = Number(item.response || 0);
        if (!Number.isNaN(parsedOops)) {
          next.oopsCount = parsedOops;
        }
      }

      if (item.time) {
        next.latestAt = item.time;
      }

      meta.set(sectionId, next);
    });

    return meta;
  }, [testData]);

  const activeSectionId = useMemo(() => {
    const orderedSections = (testData?.sections || [])
      .filter((section) => isMonitorVisibleSection(section))
      .slice()
      .sort((a, b) => (a.index || 0) - (b.index || 0));

    const recent = (testData?.realtimeResponses || [])
      .filter((item) => item?.sectionId && item?.question !== "ConnectionRestored")
      .slice()
      .sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime())[0];

    if (recent?.sectionId) {
      const recentSectionId = Number(recent.sectionId);
      const recentSection = orderedSections.find((section) => Number(section.sectionId || 0) === recentSectionId);

      // A NextSection event with 0 means the user advanced, so highlight the next unsubmitted section.
      if (recent.question === "NextSection" && Number(recent.response || 0) === 0 && recentSection) {
        const nextUnsubmitted = orderedSections.find((section) =>
          (section.index || 0) > (recentSection.index || 0) && !section.submitTime,
        );
        if (nextUnsubmitted?.sectionId) {
          return Number(nextUnsubmitted.sectionId);
        }
      }

      if (recentSection && !recentSection.submitTime) {
        return recentSectionId;
      }
    }

    const firstUnsubmitted = orderedSections.find((section) => !section.submitTime);

    if (firstUnsubmitted?.sectionId) {
      return Number(firstUnsubmitted.sectionId);
    }

    return Number(orderedSections[0]?.sectionId || 0);
  }, [testData]);

  const sectionCards = useMemo(() => {
    const bySection = new Map<number, QuestionHistory[]>();
    questionHistories.forEach((item) => {
      const existing = bySection.get(item.sectionId) || [];
      existing.push(item);
      bySection.set(item.sectionId, existing);
    });

    // Preserve taker order: API sections are already CTT Order; sort by index as tie-breaker.
    return (testData?.sections || [])
      .filter((section) => isMonitorVisibleSection(section))
      .slice()
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((section) => {
        const sectionQuestions = bySection.get(section.sectionId || 0) || [];
        const answersProvided = sectionQuestions.filter((q) => {
          const latest = q.finalPosted?.value ?? q.latestRealtime;
          return !!normalizeValue(latest).trim();
        }).length;
        const totalFromDefinition = Number(section.questionCount || 0);
        const totalQuestions = totalFromDefinition > 0 ? totalFromDefinition : Math.max(sectionQuestions.length, answersProvided);
        const started = answersProvided > 0 || sectionQuestions.some((q) => q.realtimeEntries.length > 0 || q.finalPosted);
        const completed = !!section.submitTime;
        const isActive = Number(section.sectionId || 0) === activeSectionId && !completed;

        return {
          ...section,
          questions: sectionQuestions,
          totalQuestions,
          answersReceived: answersProvided,
          completed,
          started,
          isActive,
        };
      });
  }, [questionHistories, testData, activeSectionId]);

  const selectedSection = useMemo(() => {
    const fallbackSectionId = selectedSectionId || activeSectionId;
    const selected = sectionCards.find((s) => Number(s.sectionId || 0) === Number(fallbackSectionId || 0));
    return selected || sectionCards[0] || null;
  }, [sectionCards, selectedSectionId, activeSectionId]);

  useEffect(() => {
    if (!followActiveSection || !activeSectionId) {
      return;
    }
    setSelectedSectionId(activeSectionId);
  }, [followActiveSection, activeSectionId]);

  function handleSectionCardClick(sectionId: number) {
    setFollowActiveSection(false);
    setSelectedSectionId(sectionId);
  }

  const fieldRealtimeLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    const sorted = (testData?.realtimeResponses || [])
      .filter((item) => item?.question)
      .slice()
      .sort((a, b) => new Date(a.time || 0).getTime() - new Date(b.time || 0).getTime());

    sorted.forEach((item) => {
      const dataKey = resolveRealtimeDataKey(item.question);
      if (!dataKey) return;
      lookup.set(dataKey, normalizeValue(item.response));
    });

    return lookup;
  }, [testData]);

  const instanceDataHeaderFields = useMemo(() => {
    const byKey = testData?.instanceDataActiveByKey || {};
    const siteNameById = new Map<number, string>();
    (testData?.availableSites || []).forEach((site) => {
      const siteId = Number(site.id || 0);
      if (siteId > 0) {
        siteNameById.set(siteId, normalizeValue(site.name || site.alias || site.id));
      }
    });

    const jobNameById = new Map<number, string>();
    (testData?.availableJobs || []).forEach((job) => {
      const jobId = Number(job.id || 0);
      if (jobId > 0) {
        jobNameById.set(jobId, normalizeValue(job.name || job.id));
      }
    });

    const keys = Array.from(
      new Set([
        ...Object.keys(byKey),
        ...Array.from(fieldRealtimeLookup.keys()),
      ])
    ).filter((key) => key && !SCORING_DATA_KEYS.has(key));
    const orderIndex = new Map(PREFERRED_DATA_KEY_ORDER.map((key, index) => [key, index]));

    keys.sort((a, b) => {
      const ai = orderIndex.has(a) ? orderIndex.get(a)! : 1000;
      const bi = orderIndex.has(b) ? orderIndex.get(b)! : 1000;
      if (ai !== bi) return ai - bi;
      return formatDataKeyLabel(a).localeCompare(formatDataKeyLabel(b));
    });

    return keys.map((dataKey) => {
      const staticRaw = normalizeValue(byKey[dataKey]);
      const realtimeValue = fieldRealtimeLookup.get(dataKey) || "";

      let value = realtimeValue || staticRaw;
      if (dataKey === "SiteId" && value) {
        const siteId = Number(value);
        if (Number.isFinite(siteId) && siteId > 0) {
          value = siteNameById.get(siteId) || value;
        }
      } else if (dataKey === "CompanyJobId" && value) {
        const jobId = Number(value);
        if (Number.isFinite(jobId) && jobId > 0) {
          value = jobNameById.get(jobId) || value;
        }
      } else if (dataKey === "LanguageId" && value) {
        value = normalizeLanguageLabel(value, supportedLanguages) || value;
      } else if (dataKey === "ClientIpAddress" && value) {
        value = displayClientIp(value);
      }

      return {
        id: dataKey,
        label: formatDataKeyLabel(dataKey),
        value,
      };
    });
  }, [testData, fieldRealtimeLookup, supportedLanguages]);

  const selectedSectionPageInfo = useMemo(() => {
    const sectionId = Number(selectedSection?.sectionId || 0);
    const meta = sectionRealtimeMeta.get(sectionId);
    const pageCount = Number(selectedSection?.pages || 0) || 1;
    const inferredPage = Number(meta?.currentPage || 1) || 1;
    const currentPage = selectedSection?.submitTime ? pageCount : Math.min(Math.max(inferredPage, 1), pageCount);
    const oopsCount = Number(meta?.oopsCount || 0);
    return { currentPage, pageCount, oopsCount };
  }, [selectedSection, sectionRealtimeMeta]);

  const languageLabel = useMemo(() => {
    const byKey = testData?.instanceDataActiveByKey || {};
    const realtimeLanguage = normalizeLanguageLabel(fieldRealtimeLookup.get("Language"), supportedLanguages);
    if (realtimeLanguage) return realtimeLanguage;

    const dataLanguage = normalizeLanguageLabel(byKey.Language, supportedLanguages);
    if (dataLanguage) return dataLanguage;

    const snapshotLanguage = normalizeLanguageLabel(testData?.language, supportedLanguages);
    if (snapshotLanguage) return snapshotLanguage;

    return normalizeLanguageLabel(byKey.LanguageId ?? testData?.languageId, supportedLanguages);
  }, [testData, fieldRealtimeLookup, supportedLanguages]);

  const centerCaption = useMemo(() => {
    const byKey = testData?.instanceDataActiveByKey || {};
    const firstName = normalizeValue(
      fieldRealtimeLookup.get("FirstName") || byKey.FirstName || testData?.firstName,
    ).trim();
    const lastName = normalizeValue(
      fieldRealtimeLookup.get("LastName") || byKey.LastName || testData?.lastName,
    ).trim();
    const takerName = [firstName, lastName].filter(Boolean).join(" ");
    if (takerName) {
      return takerName;
    }

    const testTypeName = normalizeValue(testData?.companyTestType?.name || testData?.testTypeName).trim();
    if (!testTypeName) return null;
    return languageLabel ? `${testTypeName} (${languageLabel})` : testTypeName;
  }, [testData, fieldRealtimeLookup, languageLabel]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="testMonitorModal"
      title={`Monitor Test${testData?.id ? ` (${testData.id})` : ""}`}
      size="xl"
      captionCenter={centerCaption}
    >
      <div className="testMonitorHeader">
        {instanceDataHeaderFields.length > 0 ? (
          instanceDataHeaderFields.map((field) => (
            <span key={field.id} className={`contextField ${field.value ? "" : "pending"}`}>
              <strong>{field.label}</strong>
              <span>{field.value || "-"}</span>
            </span>
          ))
        ) : null}
      </div>
      <div className="testMonitorTimeline">
        {sectionCards.map((section) => {
          let statusClass = "notStarted";
          if (section.completed) statusClass = "completed";
          else if (section.isActive) statusClass = "active";

          const selected = Number(selectedSection?.sectionId || 0) === Number(section.sectionId || 0);

          return (
            <button
              type="button"
              key={section.sectionId || section.index}
              className={`timelineSectionCard ${statusClass} ${selected ? "selected" : ""}`}
              onClick={() => handleSectionCardClick(Number(section.sectionId || 0))}
            >
              <div className="timelineTitle">{section.name}</div>
              <div className="timelineCounts">
                <span>{section.answersReceived}/{section.totalQuestions}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="testMonitorDetailsCard">
        <header className="detailsTopRow">
          <span><strong>Form</strong> {selectedSection?.formId || "-"}</span>
          <span><strong>Section</strong> {selectedSection?.name || "-"}</span>
          <span><strong>Page</strong> {selectedSectionPageInfo.currentPage}/{selectedSectionPageInfo.pageCount}</span>
          <span><strong>Answers</strong> {selectedSection?.answersReceived || 0}/{selectedSection?.totalQuestions || 0}</span>
          <span><strong>Oopses</strong> {selectedSectionPageInfo.oopsCount}</span>
          <span><strong>Posted</strong> {selectedSection?.submitTime ? Helpers.EST(selectedSection.submitTime) : "-"}</span>
        </header>

        <div className="testMonitorSections">
          <section className="monitorSectionCard monitorAnswerPanel">
            <div className="monitorQuestionList">
              {!selectedSection || selectedSection.questions.length === 0 ? (
                <div className="monitorEmptyState">No answer activity yet.</div>
              ) : (
                <div className="monitorAnswerColumns">
                  {selectedSection.questions
                    .slice()
                    .sort((a, b) =>
                      normalizeValue(a.questionId).localeCompare(normalizeValue(b.questionId), undefined, {
                        numeric: true,
                        sensitivity: "base",
                      })
                    )
                    .map((question) => {
                    const reverseChronological = question.realtimeEntries.slice().reverse();
                    const finalEntry = reverseChronological[0] || null;
                    const otherEntries = reverseChronological.slice(1);

                    return (
                      <article
                        className="monitorAnswerCard"
                        key={`${selectedSection.sectionId}-${question.questionId}`}
                      >
                        <span className="monitorQuestionId">
                          {buildAnswerId(selectedSection?.formId, question.questionId)}
                        </span>

                        {finalEntry ? (
                          <span
                            className="answerChip latest"
                            title={finalEntry.when ? Helpers.EST(finalEntry.when) : ""}
                          >
                            <span className="answerValue">{finalEntry.value}</span>
                          </span>
                        ) : (
                          <span className="answerChip latest empty">
                            <span className="answerValue"></span>
                          </span>
                        )}

                        <span className="monitorQuestionOthers">
                          {otherEntries.length > 0 ? (
                            otherEntries.map((entry, index) => (
                              <span
                                className="answerChip realtime"
                                key={`${question.questionId}-rt-prev-${index}`}
                                title={entry.when ? Helpers.EST(entry.when) : ""}
                              >
                                <span className="answerValue">{entry.value}</span>
                              </span>
                            ))
                          ) : (
                            <span className="answerChip realtime empty">
                              <span className="answerValue"></span>
                            </span>
                          )}
                        </span>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
}
